import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { dmPatientQueryKey } from '../../../../../hooks/useDmPatient';
import { ApiConflictError } from '../../../../../services/apiErrors';
import {
    cancelForcedAlignerChange,
    forceNextAlignerChange
} from '../../../../../services/dmMonitoringControl';
import { useDmControlMutation } from './useDmControlMutation';

interface AlignerChangeCallbacks {
    onDone: () => void;
    /** DM already holds the other state; the refetched patient flips the action. */
    onConflict: () => void;
}

// Forces the next aligner change, or withdraws a pending one, depending on `pending`.
export function useAlignerChangeRequest(
    patientId: string,
    { onDone, onConflict }: AlignerChangeCallbacks
) {
    const queryClient = useQueryClient();
    const action = useDmControlMutation(
        patientId,
        (pending: boolean) =>
            pending ? cancelForcedAlignerChange(patientId) : forceNextAlignerChange(patientId),
        onDone
    );
    const { run: mutate } = action;
    const run = useCallback(
        (pending: boolean) =>
            mutate(pending, {
                onError: (error) => {
                    if (!(error instanceof ApiConflictError)) return;
                    void queryClient.invalidateQueries({ queryKey: dmPatientQueryKey(patientId) });
                    onConflict();
                }
            }),
        [mutate, onConflict, patientId, queryClient]
    );
    return { ...action, run };
}
